import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Menu, X, ChevronDown, User, Check } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const userMenuRef = useRef(null);
  const navMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
        setOpenMenu(null);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenMenu(null);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSystem = user?.role === 'system';
  const homeHref = isSystem ? '/system/tenants' : '/dashboard';

  const navigation = isSystem
    ? [{ name: 'Tenants', href: '/system/tenants' }]
    : [
        { name: 'Dashboard', href: '/dashboard' },
        ...(user?.role === 'admin'
          ? [
              { name: 'Employees', href: '/employees' },
              { name: 'Attendance', href: '/attendance' },
              {
                name: 'QR',
                submenu: [
                  { name: 'Station display', href: '/qr-attendance' },
                  { name: 'Scan QR', href: '/scan-qr' },
                ],
              },
              {
                name: 'Leave',
                submenu: [
                  { name: 'Approvals', href: '/leave-approval' },
                  { name: 'Calendar', href: '/leave-calendar' },
                  { name: 'Yearly settings', href: '/yearly-leave-settings' },
                ],
              },
              ...(user?.isJiraEnabled ? [{ name: 'Jira', href: '/jira-time-tracking' }] : []),
              { name: 'Reports', href: '/reports' },
              { name: 'Holidays', href: '/holidays' },
              { name: 'Settings', href: '/settings' },
            ]
          : [
              { name: 'Attendance', href: '/my-attendance' },
              { name: 'Scan QR', href: '/scan-qr' },
              {
                name: 'Leave',
                submenu: [
                  { name: 'Apply leave', href: '/leave-application' },
                  { name: 'Calendar', href: '/leave-calendar' },
                ],
              },
            ]),
      ];

  const isActive = (path) => location.pathname === path;
  const isParentActive = (item) => {
    if (item.href && isActive(item.href)) return true;
    if (item.submenu) return item.submenu.some((sub) => isActive(sub.href));
    return false;
  };

  // Desktop: full-height link with a thin indicator sitting on the header border.
  const desktopLink = (active) =>
    `relative inline-flex h-14 items-center gap-1 px-3 text-sm font-medium transition-colors focus-visible:outline-none ${
      active
        ? 'text-gray-900 after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gray-900'
        : 'text-gray-500 hover:text-gray-900'
    }`;

  const mobileLink = (active) =>
    `flex min-h-11 items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
      active ? 'bg-slate-950 text-white' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
    }`;

  const initial = user?.name?.charAt(0)?.toUpperCase() || 'U';
  const subscriptionExpired =
    user?.subscriptionExpired || user?.tenantInfo?.packageLifecycle?.status === 'expired';

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
      {subscriptionExpired && (
        <div className="border-b border-amber-200 bg-amber-50 text-amber-950">
          <p className="mx-auto max-w-7xl px-3 py-2 text-xs leading-snug sm:px-6 sm:text-sm lg:px-8">
            Your subscription has expired. Please contact your administrator.
          </p>
        </div>
      )}
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-3 sm:gap-6 sm:px-6 lg:px-8">
        {/* Brand */}
        <Link
          to={homeHref}
          className="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-900 text-[13px] font-semibold text-white">
            e
          </span>
          <span className="text-[15px] font-semibold tracking-tight text-gray-900">eAMS</span>
        </Link>

        {/* Desktop navigation */}
        <nav ref={navMenuRef} className="hidden min-w-0 items-center lg:flex" aria-label="Main">
          {navigation.map((item) => {
            const active = isParentActive(item);

            if (item.submenu) {
              const open = openMenu === item.name;
              return (
                <div key={item.name} className="relative">
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpenMenu(open ? null : item.name)}
                    className={desktopLink(active)}
                  >
                    {item.name}
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {open && (
                    <div
                      role="menu"
                      className="absolute left-3 top-full z-50 mt-1 w-52 rounded-xl border border-gray-200 bg-white p-1 shadow-lg shadow-gray-900/5"
                    >
                      {item.submenu.map((subItem) => {
                        const subActive = isActive(subItem.href);
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            role="menuitem"
                            className={`flex items-center justify-between rounded-lg px-2.5 py-2 text-sm transition-colors ${
                              subActive
                                ? 'bg-gray-100 font-medium text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            {subItem.name}
                            {subActive && <Check className="h-3.5 w-3.5 text-gray-500" />}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link key={item.name} to={item.href} className={desktopLink(active)}>
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {user && (
            <button
              type="button"
              onClick={handleLogout}
              className="hidden items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 sm:inline-flex"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          )}
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
              onClick={() => setIsUserMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-full p-1 pr-1.5 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-xs font-semibold text-white">
                {initial}
              </span>
              <span className="hidden max-w-[9rem] truncate text-sm font-medium text-gray-700 md:inline">
                {user?.name}
              </span>
              <ChevronDown className="hidden h-3.5 w-3.5 text-gray-400 sm:block" />
            </button>

            {isUserMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg shadow-gray-900/5"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 px-3.5 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                    {initial}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="truncate text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>
                <div className="p-1">
                  <div className="flex items-center justify-between px-2.5 py-1.5 text-xs text-gray-500">
                    Role
                    <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium capitalize text-gray-700">
                      {user?.role || 'user'}
                    </span>
                  </div>
                  {!isSystem && (
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                    >
                      <User className="h-4 w-4 text-gray-400" />
                      Profile
                    </Link>
                  )}
                </div>
                <div className="border-t border-gray-100 p-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  >
                    <LogOut className="h-4 w-4 text-gray-400" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((v) => !v)}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors lg:hidden ${
              isMenuOpen
                ? 'border-slate-950 bg-slate-950 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
            }`}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile navigation */}
      {isMenuOpen && (
        <nav
          aria-label="Main"
          className="absolute inset-x-0 top-full max-h-[calc(100dvh-3.5rem)] overflow-y-auto border-t border-slate-200 bg-[#f8fafc] shadow-[0_24px_48px_-24px_rgba(15,23,42,0.35)] lg:hidden"
        >
          <div className="mx-auto max-w-7xl px-3 pb-4 pt-3 sm:px-6">
            {user && (
              <div className="mb-3 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-sm font-semibold text-white">
                  {initial}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-950">{user?.name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium capitalize text-slate-600">
                  {user?.role || 'user'}
                </span>
              </div>
            )}

            <p className="mb-1.5 px-1 text-[11px] font-medium text-slate-500">Navigation</p>
            <div className="space-y-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
            {navigation.map((item) => {
              if (item.submenu) {
                return (
                  <div key={item.name} className="pt-1">
                    <p className="px-3 pb-1 pt-1 text-[11px] font-medium text-slate-400">
                      {item.name}
                    </p>
                    <div className="space-y-0.5">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={mobileLink(isActive(subItem.href))}
                        >
                          {subItem.name}
                          {isActive(subItem.href) && <Check className="h-4 w-4" />}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link key={item.name} to={item.href} className={mobileLink(isActive(item.href))}>
                  {item.name}
                  {isActive(item.href) && <Check className="h-4 w-4" />}
                </Link>
              );
            })}
            </div>

            {user && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {!isSystem && (
                  <Link
                    to="/profile"
                    className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <User className="h-4 w-4 text-slate-500" />
                    Profile
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className={`flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ${
                    isSystem ? 'col-span-2' : ''
                  }`}
                >
                  <LogOut className="h-4 w-4 text-slate-500" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
